import { useCallback, useEffect, useMemo, useState } from "react";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
  debounce,
} from "@mui/material";
import {
  DataGrid,
  gridRowSelectionIdsSelector,
  useGridApiRef,
} from "@mui/x-data-grid";
import PropTypes from "prop-types";

import { useOutletApi } from "@/features/authentication";
import { formatDate, formatNumber, getErrorMessage } from "@/features/shared";
import {
  CustomDateRangePicker,
  CustomDialogTitle,
  SearchTextField,
  useSnackbar,
} from "@/features/ui";

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/**
 * One modal for every Ambil source. Everything it needs — the candidate shape,
 * the filters, the proposed lines — comes from the generic
 * `other-payment-sources/:type` endpoints, so there is no per-source branching
 * here: a new source type is a new registry entry on the backend and nothing
 * more on this side.
 */
export const PaymentSourceDialog = ({ source, outletId, onClose, onSubmit }) => {
  const apiRef = useGridApiRef();
  const outletApi = useOutletApi(outletId);
  const { showSnackbar } = useSnackbar();

  const [candidates, setCandidates] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [search, setSearch] = useState("");
  const [dueDateStart, setDueDateStart] = useState(null);
  const [dueDateEnd, setDueDateEnd] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  // Keeps the ticked documents (and their metadata) across pages.
  const [selectedById, setSelectedById] = useState({});

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearch(value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }, 500),
    []
  );

  useEffect(() => () => debouncedSetSearch.clear(), [debouncedSetSearch]);

  const loadCandidates = useCallback(async () => {
    if (!outletId) return;

    setLoading(true);
    try {
      const {
        data: { data, pageInfo },
      } = await outletApi.get(`other-payment-sources/${source.type}`, {
        params: {
          page: paginationModel.page,
          size: paginationModel.pageSize,
          ...(search && { search }),
          // The backend reads this pair as due-date bounds for candidates.
          ...(dueDateStart && { startDate: dueDateStart.format("YYYY-MM-DD") }),
          ...(dueDateEnd && { endDate: dueDateEnd.format("YYYY-MM-DD") }),
        },
      });
      setCandidates(data || []);
      setRowCount(pageInfo?.totalItems ?? 0);
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal memuat dokumen sumber"));
    } finally {
      setLoading(false);
    }
  }, [
    outletApi,
    outletId,
    source.type,
    paginationModel,
    search,
    dueDateStart,
    dueDateEnd,
    showSnackbar,
  ]);

  useEffect(() => {
    loadCandidates();
  }, [loadCandidates]);

  const handleSelectionChange = () => {
    const selection = gridRowSelectionIdsSelector(apiRef.current.state);
    setSelectedById((previous) => {
      const next = {};
      selection.forEach((row, id) => {
        next[id] = row ?? previous[id];
      });
      return next;
    });
  };

  const selectedIds = Object.keys(selectedById);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const {
        data: { data },
      } = await outletApi.post(
        `other-payment-sources/${source.type}/lines`,
        { ids: selectedIds }
      );

      onSubmit(
        (data || []).map((line) => ({
          outletChartOfAccount: {
            id: line.outletChartOfAccountId,
            code: line.accountCode,
            name: line.accountName,
          },
          amount: line.amount,
          note: "",
          sourceableType: line.sourceableType,
          sourceableId: line.sourceableId,
          sourceReferenceNumber:
            selectedById[line.sourceableId]?.referenceNumber ?? null,
          sourceLabel: source.label,
        }))
      );
      onClose();
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal mengambil dokumen sumber"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <CustomDialogTitle
        variant="h5"
        title={`Ambil ${source.label}`}
        onClose={onClose}
      />
      <DialogContent dividers>
        <Stack spacing={2}>
          <Grid container spacing={2} sx={{ alignItems: "end" }}>
            <Grid size={{ xs: 12, lg: 4 }}>
              <SearchTextField
                fullWidth
                placeholder="Cari No. Referensi / Catatan"
                onChange={(event) => debouncedSetSearch(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, lg: 8 }}>
              <CustomDateRangePicker
                startLabel="Jatuh Tempo Awal"
                endLabel="Jatuh Tempo Akhir"
                startDate={dueDateStart}
                endDate={dueDateEnd}
                maxEndDate={null}
                onStartDateChange={(date) => {
                  setDueDateStart(date);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
                onEndDateChange={(date) => {
                  setDueDateEnd(date);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }}
              />
            </Grid>
          </Grid>

          <DataGrid
            apiRef={apiRef}
            rows={candidates}
            loading={loading}
            getRowHeight={() => "auto"}
            columns={[
              {
                field: "referenceNumber",
                headerName: "No #",
                flex: 1.5,
                sortable: false,
              },
              {
                field: "transactionDate",
                headerName: "Tanggal",
                flex: 1,
                sortable: false,
                valueGetter: (value) => (value ? formatDate(value) : "-"),
              },
              {
                field: "dueDate",
                headerName: "Jatuh Tempo",
                flex: 1,
                sortable: false,
                valueGetter: (value) => (value ? formatDate(value) : "-"),
              },
              {
                field: "total",
                headerName: "Total",
                flex: 1.2,
                type: "number",
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
              },
              {
                field: "outstanding",
                headerName: "Sisa",
                flex: 1.2,
                type: "number",
                sortable: false,
                valueFormatter: (value) => formatNumber(value),
              },
              {
                field: "note",
                headerName: "Keterangan",
                flex: 2,
                sortable: false,
                valueGetter: (note) => note || "-",
              },
            ]}
            checkboxSelection
            keepNonExistentRowsSelected
            onRowSelectionModelChange={handleSelectionChange}
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            disableRowSelectionOnClick
            sx={{ minHeight: 320 }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Batal</Button>
        <Button
          variant="contained"
          disabled={selectedIds.length === 0 || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Memuat..." : "Lanjut"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

PaymentSourceDialog.propTypes = {
  source: PropTypes.shape({
    type: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  }).isRequired,
  outletId: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
